// MapLegend.jsx
import React, { useState } from 'react';
import { FACILITY_TYPE_OPTIONS } from '../../api/facilitiesApi';
import { TYPE_COLORS } from './mockData/GeospatialData';

// Same full type set FilterBar exposes, so every colour on the map has an entry here.
const LEGEND_ITEMS = [
  ...FACILITY_TYPE_OPTIONS.map(({ label }) => ({ type: label, label })),
  { type: 'Market', label: 'Market' },
  { type: 'Processing', label: 'Processing' },
];

export default function MapLegend({
  activeType,
  onTypeClick,
  locationGroups = [],
  totalPoints = 0,
  onFocusGroup,
  theme = 'dark',
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? LEGEND_ITEMS : LEGEND_ITEMS.slice(0, 5);
  const isLight = theme === 'light';

  return (
    <div
      className="absolute bottom-5 left-4 z-[1002] rounded-xl px-4 py-3 min-w-[180px] max-w-[220px] border"
      style={
        isLight
          ? { background: 'rgba(255,255,255,0.95)', borderColor: 'rgba(0,0,0,0.08)', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', backdropFilter: 'blur(8px)' }
          : { background: 'rgba(15,23,42,0.92)', borderColor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }
      }
    >
      <p className={`m-0 mb-2.5 text-[10px] font-bold uppercase tracking-widest ${isLight ? 'text-gray-400' : 'text-white/50'}`}>
        Map Details
      </p>

      <div className="flex flex-col gap-0.5 max-h-[180px] overflow-y-auto pr-1">
        {visibleItems.map(({ type, label }) => {
          const isActive = !activeType || activeType === type;
          return (
            <button
              key={type}
              onClick={() => onTypeClick(type)}
              className={`flex items-center gap-2 w-full bg-transparent border-none cursor-pointer py-1 transition-opacity duration-200 ${isActive ? 'opacity-100' : 'opacity-40'}`}
            >
              {/* Colour dot — dynamic colour must stay as style */}
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{
                  background: TYPE_COLORS[type],
                  boxShadow: isActive ? `0 0 6px ${TYPE_COLORS[type]}99` : 'none',
                }}
              />
              <span className={`text-xs font-medium text-left ${isLight ? 'text-gray-700' : 'text-white/85'}`}>{label}</span>
            </button>
          );
        })}
      </div>

      {LEGEND_ITEMS.length > 5 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`mt-1.5 text-[11px] font-semibold bg-transparent border-none cursor-pointer p-0 ${isLight ? 'text-green-600 hover:text-green-700' : 'text-green-400 hover:text-green-300'}`}
        >
          {expanded ? 'Show less' : `+ ${LEGEND_ITEMS.length - 5} more`}
        </button>
      )}

      {activeType && (
        <div className={`mt-2.5 pt-2.5 border-t ${isLight ? 'border-gray-100' : 'border-white/10'}`}>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className={`m-0 text-[10px] font-bold uppercase tracking-widest truncate ${isLight ? 'text-gray-400' : 'text-white/50'}`}>
              {totalPoints} location{totalPoints === 1 ? '' : 's'}
            </p>
            {locationGroups.length > 1 && (
              <button
                onClick={() => onFocusGroup?.(locationGroups.flatMap((group) => group.points))}
                className={`shrink-0 text-[11px] font-semibold bg-transparent border-none cursor-pointer p-0 ${isLight ? 'text-green-600 hover:text-green-700' : 'text-green-400 hover:text-green-300'}`}
              >
                Show all
              </button>
            )}
          </div>

          {totalPoints === 0 ? (
            <p className={`m-0 text-[11px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
              No mapped {activeType} records yet.
            </p>
          ) : (
            <div className="flex flex-col gap-0.5 max-h-[120px] overflow-y-auto pr-1">
              {locationGroups.map(({ state, points }) => (
                <button
                  key={state}
                  onClick={() => onFocusGroup?.(points)}
                  title={`Zoom to ${activeType} in ${state}`}
                  className={`flex items-center justify-between gap-2 w-full rounded-md border-none cursor-pointer px-1.5 py-1 text-left transition-colors ${isLight ? 'bg-transparent hover:bg-gray-100' : 'bg-transparent hover:bg-white/10'}`}
                >
                  <span className={`text-xs font-medium truncate ${isLight ? 'text-gray-700' : 'text-white/85'}`}>{state}</span>
                  <span
                    className="shrink-0 rounded-full px-1.5 text-[10px] font-bold text-white"
                    style={{ background: TYPE_COLORS[activeType] ?? '#16a34a' }}
                  >
                    {points.length}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
